// Added by Sejune Oh on 2019-02-15
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { Component, createRef } from 'react'
import { IntlProvider } from 'react-intl'
import styled from 'styled-components'
import { inject, observer, disposeOnUnmount } from 'mobx-react'
import { observable, action, reaction, computed, makeObservable } from 'mobx'
import IdleTimer from 'react-idle-timer'
import { BrowserRouter } from 'react-router-dom'
import { Route } from 'react-router'
import flatten from 'flat'
import jwtDecode from 'jwt-decode'
import { KeyIndexedObject } from './helper/define'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Theme
import provideTheme from './helper/provideTheme'

// Define
import {
  AUTH_TOKEN_COOKIE,
  IDLE_TO_ACTION_DELAY,
  COOKIE_IDLE_TIMER,
  COOKIE_LAST_ACTIVE_TIME,
  DEFAULT_LANG,
  HVN_INTEGRATED,
} from './helper/define'

// Logger
import Logger from './helper/Logger'

// Util
import { getCookie, setCookie, deleteCookie, getMilliSeconds } from './helper/util'
import Intl from './helper/Intl'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Store
import { UserAuthStore } from './stores/user/UserAuth'
import EventStore from './stores/EventStore'
import { SiteConfigStore, PackageConfigStore } from './stores/mgmt/config'
import { HistoryStore } from './stores/global'
import ProductStore from './stores/ProductStore'
import CLAppStore from './stores/CLAppStore'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ! WebComponents
import '@/components/worklist/WebComponents'

// Prime elements
import { Snackbar, SnackbarContent } from './components/_primes'

// Componnet
import RootRouter from './RootRouter'
import Dialog from './components/common/Dialog'
import NotificationsDrawer from './components/common/v2/NotificationsDrawer'
import { ScreenLock } from './components/login'
import { NotiBar } from './components/common/NotiBar'
import { NotiStackProvider } from '@/components/common/v2/NotiStack'
import { UserStore } from './stores/mgmt/user'
import { initPacsQR } from './service/pacsqr.service'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
interface RootAppCProps {
  user?: UserStore
  userAuth?: UserAuthStore
  globalEvt?: EventStore
  siteConfig?: SiteConfigStore
  packageConfig?: PackageConfigStore
  historyCtr?: HistoryStore
  product?: ProductStore
  clApp?: CLAppStore
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
@inject('user', 'userAuth', 'globalEvt', 'siteConfig', 'packageConfig', 'historyCtr', 'product', 'clApp')
@observer
class RootApp extends Component<RootAppCProps> {
  isFullyMounted: boolean = false
  enableScreenLock: boolean = false
  showScreenLock: boolean = false
  timeToLockScreen: number = 1
  timeToDisconnectSession: number = 1
  productConfigLoaded: boolean = false
  hvnIntegrated: boolean = false

  _idleTimer?: IdleTimer
  _timeOutForRefreshAppToken: any
  _intervalForDisconnectSession: any
  _screenLockRef = createRef<any>()
  _curI18nContainer?: KeyIndexedObject<any>
  

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  constructor(props: RootAppCProps) {
    super(props)
    makeObservable(this, {
      isFullyMounted: observable,
      enableScreenLock: observable,
      showScreenLock: observable,
      timeToLockScreen: observable,
      timeToDisconnectSession: observable,
      productConfigLoaded: observable,
      hvnIntegrated: observable,
      _handleAfterSignup: action,
      _hadleAfterSignoff: action,
      _setAccessTokenRefreshTimeOut: action,
      _clearAccessTokenRefreshTimeOut: action,
      _checkExistedUserAcessToken: action,
      _getConfig: action,
      _initScreenLock: action,
      _clearScreenLock: action,
      _onActive: action,
      _onIdle: action,
      _setDisconnectSessionInterval: action,
      _clearDisconnectSessionInterval: action,
      setScreenLock: action,
      userAuth: computed,
      product: computed,
    })
  }

  async componentDidMount() {
    // Save location path
    // eslint-disable-next-line
    this.props.historyCtr!.enqueueHistory({ pathName: location.pathname, search: location.search })

    // Get product config
    await this.product.init()

    // Initialize locale
    await this._initLocale(DEFAULT_LANG)

    disposeOnUnmount(this, [
      reaction(
        () => this.userAuth.isSignedUp,
        async (signUpStatus) => {
          if (signUpStatus) await this._handleAfterSignup()
          else await this._hadleAfterSignoff()
        }
      ),
      // Get Current User Info
      reaction(
        () => this.userAuth.userId,
        async (userId) => {
          if (userId) {
            await this.props.user?.initStore(userId)
          }
        }
      )
    ])

    await this._checkExistedUserAcessToken()

    // To integrate with the havana project
    const params = new URL(location.href).searchParams
    this.hvnIntegrated = !!params.get('hvn-integrated')
    localStorage.setItem(HVN_INTEGRATED, this.hvnIntegrated ? 'true' : '' );
    
    this.productConfigLoaded = true
    this.isFullyMounted = true
    window.addEventListener('beforeunload', this._handleBeforeUnload, true)
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this._handleBeforeUnload, true)
  }

  /** Notification drawer open/close handler */
  handleNotifications = (flag: boolean) => this.props.globalEvt!.setShowNotifications(flag)

  handleOnClose = (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason) this.handleNotifications(false)
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  render() {
    const { globalEvt } = this.props
    if (!this.productConfigLoaded) return null

    return (
      <IntlProvider locale={this.userAuth.fullUILang} messages={this._curI18nContainer}>
        <NotiStackProvider>
          {this.enableScreenLock && (
            <IdleTimer
              ref={(ref: IdleTimer) => (this._idleTimer = ref)}
              element={document}
              onActive={this._onActive}
              onIdle={this._onIdle}
              onAction={this._onAction}
              debounce={IDLE_TO_ACTION_DELAY}
              timeout={this.timeToLockScreen}
            />
          )}

          {this.isFullyMounted && (
            <RootSection>
              <BrowserRouter>
                <Route component={RootRouter}></Route>
              </BrowserRouter>

              {/** Loading for Global Event */}
              {globalEvt!.isOnLoading && (
                <LoadingWrapper id="loading-wrapper">
                  <LoadingImg style={{ backgroundImage: this.props.product!.loadingGif }} />
                </LoadingWrapper>
              )}

              <ScreenLock ref={this._screenLockRef} callback={this._afterCheckUserAuth} />

              <Dialog
                ref={globalEvt!.dialogRef}
                onConfirm={globalEvt!.handleDialogConfirm}
                onCancel={globalEvt!.handleDialogCancel}
              />

              {/** Notifications List Drawer */}
              {this.userAuth.isSignedUp && !this.hvnIntegrated && (
                <NotificationsDrawer
                  anchor="right"
                  open={globalEvt!.showNotifications}
                  setOpen={() => this.handleNotifications(true)}
                  onClose={this.handleOnClose}
                />
              )}

              {/** Global Noti */}
              <Snackbar
                anchorOrigin={{
                  vertical: globalEvt!.vertical,
                  horizontal: globalEvt!.horizontal,
                }}
                open={globalEvt!.isEvtOccured}
                onClose={globalEvt!.finishEvent}
                autoHideDuration={globalEvt!.evtValidTime}
              >
                <GlobalNoti
                  data-noti-type={globalEvt!.evtType}
                  evtType={globalEvt!.evtType}
                  message={globalEvt!.message}
                  onClose={globalEvt!.finishEvent}
                />
              </Snackbar>
              {this.userAuth!.isSignedUp && <NotiBar></NotiBar>}
            </RootSection>
          )}
        </NotiStackProvider>
      </IntlProvider>
    )
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  get userAuth() {
    return this.props.userAuth!
  }

  get product() {
    return this.props.product!
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _handleAfterSignup = () => {
    /** Signout 되어도 여전히 Loaded 상태이므로 아래 작업 완료 전까지는 렌더시키지 않는다 */
    this.productConfigLoaded = false

    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      await this._getConfig()
      this._initPacsQRServer()
      Logger.devLog(`User(${this.userAuth.userName}) has signed in. (${new Date().toISOString()})`)
      this._setAccessTokenRefreshTimeOut()
      this._initScreenLock()
      this.productConfigLoaded = true
      resolve(true)
    })
  }

  _hadleAfterSignoff = () => {
    return new Promise((resolve) => {
      this.props.globalEvt!.setLoadingOff()
      this._clearAccessTokenRefreshTimeOut()
      this._clearScreenLock()
      resolve(true)
    })
  }

  _setAccessTokenRefreshTimeOut = () => {
    if (this._timeOutForRefreshAppToken) return

    // Access Token 갱신 주기
    const clientAccessTokenTimeout = this.props.userAuth!.tokenRefreshTimeOut * 1000
    Logger.devLog(
      `- AccessTokenRefreshTimeout > Set timeOutForRefreshAppToken (${
        this.props.userAuth!.tokenRefreshTimeOut
      } seconds)`
    )

    this._timeOutForRefreshAppToken = setTimeout(async () => {
      const authTokenCookie = getCookie(AUTH_TOKEN_COOKIE)
      Logger.networkLog('- AccessTokenRefreshTimeout > Try to refresh token at ' + Date.now())
      this._clearAccessTokenRefreshTimeOut()

      try {
        if (!authTokenCookie) {
          Logger.devLog('- AccessTokenRefreshTimeout > No available token.')
          return
        }

        await this.userAuth.refreshAccessToken({
          authTokenCookie,
          force: true,
        })
        this._setAccessTokenRefreshTimeOut()
      } catch (e) {
        Logger.errLog('- AccessTokenRefreshTimeout > Failed to refresh token.')
        console.log(e)
      }
    }, clientAccessTokenTimeout)
  }

  _clearAccessTokenRefreshTimeOut = () => {
    if (!this._timeOutForRefreshAppToken) return

    Logger.devLog('- AccessTokenRefreshTimeout > Clear timeOutForRefreshAppToken')
    clearTimeout(this._timeOutForRefreshAppToken)
    this._timeOutForRefreshAppToken = null
  }

  _checkExistedUserAcessToken = async () => {
    // eslint-disable-next-line
    const params = new URL(location.href).searchParams
    try {
      // Check external access token
      const externalAccessToken = params.get('access-token') || params.get('token')
      if (externalAccessToken) {
        await this.userAuth.signInWithAccessToken(externalAccessToken)
        return
      }
    } catch (err) {
      // Failed
      Logger.errLog(`can't sign in with external access token: ${err}`)
    }

    if (!this.userAuth.isSignedUp) {
      try {
        // Check previous access token
        const authTokenCookie = getCookie(AUTH_TOKEN_COOKIE)
        let externalUser = false
        if (authTokenCookie) {
          const { payload } = jwtDecode<any>(authTokenCookie)
          if (payload) {
            externalUser = payload.external
          }
        }

        // externel or temp token이 아닌 경우에만 refresh token을 수행한다
        if (!externalUser) {
          await this.userAuth.refreshAccessToken({
            authTokenCookie,
            force: true,
          })
        }
      } catch (err) {
        // Failed
        Logger.errLog(`can't refresh the previous access token: ${err}`)
      }
    }

    if (!this.userAuth.isSignedUp) {
      // Check link token
      try {
        const linkAccessToken = params.get('link')
        // Check link access token
        if (linkAccessToken) {
          await this.userAuth.signInWithLinkToken(linkAccessToken)
          return
        }
      } catch (err) {
        // Failed
        Logger.errLog(`can't sign in with link token: ${err}`)
      }
    }

    if (!this.userAuth.isSignedUp) return

    await this._getConfig()
    this._initPacsQRServer()
    this._initScreenLock()
  }

  _getConfig = async () => {
    await Promise.all([this._getSiteConfig(), this._getPackageConfig(), this._initLocale()])

    this.props.globalEvt!.setConfigLoadingFlag(true)
  }

  _initPacsQRServer = () => {
    const qrscpBaseUrlConfig = this.props.packageConfig?.elements.filter(({ key }) => key === 'Interface.NCCServerURL')?.[0]
    if (qrscpBaseUrlConfig) {
      const { value: baseUrl } = qrscpBaseUrlConfig
      initPacsQR(baseUrl)
    }
  }

  _initScreenLock = () => {
    if (this._idleTimer) return
    Logger.devLog('- Screen Lock > Init')

    // ScreenLock 사용 여부
    const screenlockTimeout = this.props.siteConfig!.GetSiteConfigByKey('UserSession.ClientScreenLockTimeout')
    this.enableScreenLock = screenlockTimeout !== '0'
    Logger.devLog(`- Screen Lock > Status : ${this.enableScreenLock ? 'Active' : 'Inactive'}`)

    if (!this.enableScreenLock) {
      deleteCookie(COOKIE_IDLE_TIMER)
      return
    }

    // ScreenLock 까지 걸리는 시간 (Minutes)
    this.timeToLockScreen = getMilliSeconds({
      // prettier-ignore
      time: parseFloat(screenlockTimeout!),
      unit: 'm',
      defaultTime: 1000 * 60 * 30,
    })

    // ! Session 완전 만료시간 (minutes)
    const timeToDisconnectSessionFromConfig = this.props.siteConfig!.GetSiteConfigByKey('UserSession.ClientScreenLock_ValidityPeriod')
    const timeToDisconnectSession = parseFloat(timeToDisconnectSessionFromConfig!)

    this.timeToDisconnectSession = getMilliSeconds({
      time: timeToDisconnectSession,
      unit: 'm',
      defaultTime: 1000 * 60 * 30,
    })

    const prevTimerCookie = getCookie(COOKIE_IDLE_TIMER)
    if (!prevTimerCookie) return

    // 이전에 스크린 락이 걸렸던 시간 이후로 Session 완전 만료 시간만큼 지난 경우
    if (Date.now() - parseInt(prevTimerCookie) > this.timeToDisconnectSession) {
      Logger.devLog('- Screen Lock > Find previous idle timer history. Session already expired.')
      this.userAuth!.signOut()
      return
    }

    Logger.devLog(`- Screen Lock > Find previous idle timer history. (${new Date(parseInt(prevTimerCookie))}))`)
    this.setScreenLock(true, parseInt(getCookie(COOKIE_LAST_ACTIVE_TIME)!))
  }

  _clearScreenLock = () => {
    Logger.devLog('- Screen Lock > Clear')
    this.setScreenLock(false)
    this._clearDisconnectSessionInterval()
    this.enableScreenLock = false
  }

  // # Idle 상태에서 Action이 일어나면 그 즉시 Active 상태로 변경됩니다.
  _onActive = () => {
    Logger.devLog('- Screen Lock > Change state <ACTIVE>')
  }

  // # 마지막 Action으로 부터 timeout 만큼의 시간 동안 아무 동작이 없을 경우 Idle 상태로 간주합니다.
  _onIdle = () => {
    Logger.devLog('- Screen Lock > Change state <IDLE>')
    this.setScreenLock(true, this._idleTimer!.getLastActiveTime())
  }

  _setDisconnectSessionInterval = async () => {
    if (this._intervalForDisconnectSession) return

    Logger.devLog(`- Screen Lock > Set Max session interval (${this.timeToDisconnectSession / 1000} seconds)`)

    this._intervalForDisconnectSession = setInterval(async () => {
      Logger.devLog(
        `- Screen Lock > ${this._idleTimer!.getElapsedTime() /
          1000} seconds passed after screen lock. Session has been expired`
      )
      this._clearAccessTokenRefreshTimeOut()
      this._clearScreenLock()

      const title = Intl.getIntlText({ id: 'htmljs_react.worklist.rootapp.dialog_title_session_expired' })
      const description = Intl.getIntlText({ id: 'htmljs_react.worklist.rootapp.dialog_desc_session_expired' })
      const confirmBtn = Intl.getIntlText({ id: 'htmljs_react.worklist.rootapp.dialog_btn_okay' })
      this.props.globalEvt!.openDialog(
        {
          title,
          description,
          confirmBtn,
          hideCancelBtn: true,
          disableBackdropClick: true,
        },
        () => {
          return new Promise((resolve) => resolve(true))
        }
      )
      if (this.userAuth.isSignedUp) await this.userAuth.signOut()
    }, this.timeToDisconnectSession)
  }

  _clearDisconnectSessionInterval = () => {
    if (!this._intervalForDisconnectSession) return

    Logger.devLog('- Screen Lock > Clear max session interval')
    clearInterval(this._intervalForDisconnectSession)
    this._intervalForDisconnectSession = undefined
  }

  setScreenLock = (flag: boolean, lastActiveTime?: number) => {
    this.showScreenLock = flag

    // Close screen lock
    if (!flag) {
      deleteCookie(COOKIE_IDLE_TIMER)
      deleteCookie(COOKIE_LAST_ACTIVE_TIME)
      this._clearDisconnectSessionInterval()
      if (this._screenLockRef!.current) {
        this._screenLockRef.current!.closeScreenLock()
        document.dispatchEvent(new Event('screen-lock-closed'))
      }
      return
    }

    // Set screen lock
    setCookie({
      name: COOKIE_IDLE_TIMER,
      value: `${Date.now()}`,
      expHours: this.timeToDisconnectSession,
    })
    setCookie({
      name: COOKIE_LAST_ACTIVE_TIME,
      value: `${lastActiveTime}`,
      expHours: this.timeToDisconnectSession,
    })
    this._setDisconnectSessionInterval()

    if (this._screenLockRef!.current) {
      this._screenLockRef.current!.showScreenLock(lastActiveTime)
      return
    }

    Logger.devLog('- Screen Lock > Waitng for screenlock mounted')
    let retryCount = 1

    // Screen Lock UI가 Mount 되기까지 sync 하는 Interval
    const tempInterval = setInterval(() => {
      Logger.devLog(`- Screen Lock > retryCount : ${retryCount++}`)
      if (this._screenLockRef!.current) {
        this._screenLockRef.current!.showScreenLock(lastActiveTime)
        clearInterval(tempInterval)
      }
    }, 500)
  }
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _handleBeforeUnload = (e: BeforeUnloadEvent) => {
    if (this.props.clApp!.isPromptRequired) {
      e.returnValue = this.props.clApp!.promptMessage
    }
  }

  _getSiteConfig = async () => {
    return await this.props.siteConfig!.initialize()
  }

  _getPackageConfig = async () => {
    const getPath = this.props.packageConfig!.getQueryPath({})
    return await this.props.packageConfig!.getElements(getPath)
  }

  _initLocale = async (lang?: string) => {
    let langCode = lang || this.userAuth.fullUILang

    let localeData
    try {
      localeData = await this.props.product!.initLocaleData(langCode)
    } catch (e) {
      console.error('Failed to initialize locale data. It\'ll be using default language(en-US).')
      langCode = DEFAULT_LANG
      this.userAuth.changeLang(langCode)
      localeData = await this.props.product!.initLocaleData(langCode)
    }
    this._curI18nContainer = flatten(localeData.i18n)

    document.documentElement.style.fontFamily = document.body.style.fontFamily = localeData.$fontFamily
    document.documentElement.style.fontSize = document.body.style.fontSize = localeData.$fontSize
    const w = window as any
    w.__clapp_i18n_data = localeData.i18n
    w.__clapp_i18n_langcode = langCode
  }

  _afterCheckUserAuth = () => {
    this.setScreenLock(false)
  }

  // # 마지막 이벤트가 종료되는 시점으로 부터 IDLE_TO_ACTION_DELAY 만큼의 시간 이후에 호출되는 메소드 입니다.
  _onAction = (e: Event) => {
    if (!this.userAuth.isSignedUp || this.showScreenLock) return
    Logger.devLog(`- Screen Lock > Action(${e.type})`)
  }
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const RootSection = styled.section`
  overflow: hidden;
`

export const LoadingWrapper = styled.span`
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  margin: auto;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1301;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 1;
  transition: opacity 0.5s linear;
`

export const LoadingImg = styled.span`
  && {
    width: 111px !important;
    height: 109px !important;
    opacity: 0.6;
    background-repeat: no-repeat;
    background-size: 100%;
  }
`

const GlobalNoti = styled(SnackbarContent)`
  && {
    color: #fff;

    &[data-noti-type='error'] {
      background-color: #f44336;
    }
    &[data-noti-type='success'] {
      background-color: #43a047;
    }
    &[data-noti-type='info'] {
      background-color: #145c82;
    }
    &[data-noti-type='warning'] {
      background-color: #ffa000;
    }
  }
`

export default provideTheme(RootApp)
